import React, { useState, useEffect } from 'react';
import { Users, Briefcase, TrendingUp, Fuel, Cog } from 'lucide-react';
import { Button } from './ui/button';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const MostBooked = ({ t, onBookCar, language }) => {
  const [featuredCars, setFeaturedCars] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchCars = async () => {
      try {
        const response = await fetch(`${BACKEND_URL}/api/cars/available`);
        if (response.ok) {
          const allCars = await response.json();
          // Get Skoda Superb (id: 2), Volkswagen Passat (id: 1), Mercedes Vito (id: 6)
          const featuredCarIds = [2, 1, 6];
          const filtered = allCars.filter(car => featuredCarIds.includes(car.id));
          filtered.sort((a, b) => a.pricePerDay - b.pricePerDay);
          setFeaturedCars(filtered);
        }
      } catch (error) {
        console.error('Failed to fetch cars:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchCars();
  }, []);

  const sectionTitle = language === 'ro' ? 'Cele mai rezervate săptămâna aceasta' : 'Most booked this week';
  const sectionSubtitle = language === 'ro' ? 'Alegerile preferate ale clienților noștri' : 'Our customers\' top choices';

  if (loading) {
    return (
      <section className="py-16 sm:py-20 bg-[#ECFDF5]">
        <div className="container mx-auto px-4 sm:px-6 lg:px-12">
          <div className="text-center py-12">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-4 border-[#10B981] border-t-transparent"></div>
          </div>
        </div>
      </section>
    );
  }

  if (featuredCars.length === 0) {
    return null;
  }

  return (
    <section className="py-16 sm:py-20 bg-white">
      <div className="container mx-auto px-4 sm:px-6 lg:px-12">
        {/* Section Header */}
        <div className="text-center mb-12">
          <div className="flex items-center justify-center space-x-2 mb-3">
            <TrendingUp className="text-[#10B981]" size={20} />
            <p className="text-[#10B981] text-sm font-semibold tracking-[0.1em] uppercase">
              Popular
            </p>
          </div>
          <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold text-[#0D1B3E]">
            {sectionTitle}
          </h2>
        </div>

        {/* Featured Cars Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8 max-w-6xl mx-auto">
          {featuredCars.map((car) => (
            <div 
              key={car.id} 
              data-testid={`most-booked-${car.id}`}
              className="group bg-white rounded-xl shadow-md overflow-hidden hover:shadow-xl transition-all duration-300 border border-gray-100"
            >
              {/* Image Container */}
              <div className="relative h-48 sm:h-52 overflow-hidden bg-gray-100">
                <img
                  src={car.image}
                  alt={car.name}
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                  onError={(e) => {
                    e.target.style.display = 'none';
                  }}
                />
                {/* Price Tag */}
                <div className="absolute bottom-3 right-3 bg-white/95 backdrop-blur-sm px-3 py-1.5 rounded-lg shadow-md">
                  <span className="font-bold text-[#0D1B3E] text-sm">€{car.pricePerDay}</span>
                  <span className="text-gray-500 text-xs ml-1">/{language === 'ro' ? 'zi' : 'day'}</span>
                </div>
              </div>
              
              {/* Content */}
              <div className="p-5 sm:p-6">
                <p className="text-[#10B981] text-xs font-semibold tracking-wider uppercase mb-1">{car.category}</p>
                <h3 className="font-display text-lg sm:text-xl font-bold text-[#0D1B3E] mb-3">{car.name}</h3>

                {/* Specs */}
                <div className="grid grid-cols-2 gap-2 mb-5 text-gray-500 text-sm">
                  <div className="flex items-center gap-1.5">
                    <Users size={14} className="text-[#10B981]" />
                    <span>{car.passengers}</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <Briefcase size={14} className="text-[#10B981]" />
                    <span>{car.luggage}</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <Cog size={14} className="text-[#10B981]" />
                    <span>{car.transmission}</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <Fuel size={14} className="text-[#10B981]" />
                    <span>{car.fuel}</span>
                  </div>
                </div>

                <Button
                  onClick={() => onBookCar(car)}
                  data-testid={`book-featured-${car.id}`}
                  className="w-full bg-[#0D1B3E] hover:bg-[#1A3A6B] text-white font-semibold py-4 rounded transition-all"
                >
                  {t.fleet.bookNow}
                </Button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default MostBooked;
